import React from 'react';
import Icon from '../../../components/AppIcon';

const ScoreBreakdown = ({ parameters = [], overallScore, deltaVsPrevious, interviewType }) => {
  if (!parameters || parameters.length === 0) return null;

  // Weight maps per interview type — if parameter name doesn't match ANY known weight,
  // all parameters get equal weight (prevents wrong formula for Resume/Behavioral interviews)
  const CODING_WEIGHTS = {
    'Coding Skills': 0.30,
    'Problem Solving': 0.30,
    'Communication': 0.15,
    'Confidence': 0.10,
    'Time Management': 0.15,
  };

  // Check if parameters match the coding weight map
  const knownParams = parameters.filter(p => CODING_WEIGHTS[p.name]);
  const useEqualWeights = knownParams.length < parameters.length * 0.5; // If < 50% match, use equal weights

  const equalWeight = 1 / parameters.length;
  const weights = useEqualWeights
    ? Object.fromEntries(parameters.map(p => [p.name, equalWeight]))
    : CODING_WEIGHTS;

  // Calculate theoretical weighted score
  let weightedTotal = 0;
  let totalWeight = 0;
  const breakdown = parameters.map(p => {
    const w = weights[p.name] || (1 / parameters.length);
    const contribution = Math.round(p.score * w);
    weightedTotal += p.score * w;
    totalWeight += w;
    return { ...p, weight: w, contribution };
  });

  // Normalize if weights don't sum to 1
  if (totalWeight > 0 && totalWeight !== 1) {
    weightedTotal = weightedTotal / totalWeight;
  }

  const paramDeltas = deltaVsPrevious?.parameters || {};

  const handleRefClick = (refId) => {
      const el = document.getElementById(`transcript-turn-${refId}`);
      if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-emerald-400', 'ring-offset-2');
          setTimeout(() => el.classList.remove('ring-2', 'ring-emerald-400', 'ring-offset-2'), 3000);
      }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 65) return 'text-sky-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const getContainerSkin = (score) => {
    if (score >= 80) return 'bg-emerald-50/30 border-emerald-100/40';
    if (score >= 65) return 'bg-sky-50/30 border-sky-100/40';
    if (score >= 50) return 'bg-amber-50/30 border-amber-100/40';
    return 'bg-red-50/30 border-red-100/40';
  };

  const getBarColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 65) return 'bg-sky-500';
    if (score >= 50) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="bg-white border border-slate-100 rounded-[2rem] p-6 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-slate-900 text-white shadow-sm">
            <Icon name="Calculator" size={16} />
          </div>
          <div>
            <h3 className="text-xs font-semibold text-slate-900 tracking-[0.15em]">Detailed Performance Breakdown</h3>
            <p className="text-[10px] text-slate-400 font-medium mt-0.5">How your overall score was calculated</p>
          </div>
        </div>

        {/* Computed vs Actual comparison */}
        <div className="flex items-center gap-3">
          <div className="text-right">
            <span className="text-[8px] font-medium text-slate-400 tracking-widest block">Formula Result</span>
            <span className={`text-lg font-semibold ${getScoreColor(Math.round(weightedTotal))}`}>{Math.round(weightedTotal)}</span>
          </div>
          {overallScore !== undefined && Math.abs(overallScore - Math.round(weightedTotal)) > 2 && (
            <>
              <Icon name="ArrowRight" size={12} className="text-slate-300" />
              <div className="text-right">
                <span className="text-[8px] font-medium text-slate-400 tracking-widest block">Final</span>
                <span className={`text-lg font-semibold ${getScoreColor(overallScore)}`}>{overallScore}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Formula visualization */}
      <div className="bg-slate-50 rounded-xl p-3 mb-5 border border-slate-100">
        <p className="text-[10px] font-mono text-slate-500 text-center leading-relaxed">
          Overall = {breakdown.map((b, i) => (
            <span key={i}>
              {i > 0 && ' + '}
              <span className="text-slate-700 font-medium">{b.name.split(' ').map(w => w[0]).join('')}</span>
              <span className="text-slate-400">×{Math.round(b.weight * 100)}%</span>
            </span>
          ))}
        </p>
      </div>

      {/* Parameter bars */}
      <div className="space-y-4">
        {breakdown.map((param, idx) => {
          const delta = paramDeltas[param.name];
          return (
            <div key={idx} className={`border rounded-2xl p-4 sm:p-5 transition-all duration-300 ${getContainerSkin(param.score)}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-base font-semibold text-slate-900">{param.name}</span>
                  <span className="text-[10px] font-medium text-slate-400 bg-slate-50 border border-slate-100 rounded-md px-1.5 py-0.5">
                    ×{Math.round(param.weight * 100)}%
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {/* Delta from last interview */}
                  {delta !== undefined && delta !== 0 && (
                    <span className={`text-xs font-medium flex items-center gap-0.5 ${delta > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      <Icon name={delta > 0 ? 'TrendingUp' : 'TrendingDown'} size={12} />
                      {delta > 0 ? '+' : ''}{delta}
                    </span>
                  )}
                  <span className={`text-2xl font-semibold ${getScoreColor(param.score)}`}>{param.score}</span>
                  <span className="text-xs text-slate-400 font-medium mt-1">
                    → {param.contribution}pts
                  </span>
                </div>
              </div>
              
              {/* Sub-text: transcript evidence */}
              {param.feedback && (
                <p className="text-sm text-slate-600 font-normal mt-2 leading-relaxed">{param.feedback}</p>
              )}
              {param.transcriptRef && param.transcriptRef > 0 && (
                <button
                    onClick={() => handleRefClick(param.transcriptRef)}
                    className="flex items-center gap-1 mt-1.5 text-[9px] font-medium text-emerald-500 tracking-widest hover:text-emerald-700 transition-colors"
                >
                    <Icon name="ExternalLink" size={9} />
                    View evidence in transcript
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ScoreBreakdown;
